<script setup lang="ts">
import { useElementSize } from '@vueuse/core'

import { useCustomerStore } from '@stores/me/customer'
import { useKidStore } from '@stores/kid'
import { useSubscriptionStore } from '@stores/subscription'
import { useCommonStore } from '@stores/common'

import { useSettings } from '@hooks/settings/index.hook'
import { useLists } from '@hooks/lists/index.hook'
import { useSubscriptionList } from '@hooks/subscription/subscription-list.hook'
import { useSubscriptionFailedApi } from '@composables/api/subscription/subscription-failed.hook'
import { useStoreSubscriptionActions } from '@composables/api/subscription/storage-subscription-banner.hook'
import { useScreenWidth } from '@hooks/feature-tour/screen-width.hook'
import { useListenCustomerChannel } from '@ws/listen-customer-channel.hook'
import { useListenDisciplineChannel } from '@ws/listen-discipline-channel.hook'
import { useEcho } from '@composables/tools/echo.hook'
import { useUpsellBannerLoad } from '@hooks/upsell-banner/upsell-banner-load.hook'

import MobileFooter from '@components/Widgets/MobileFooter.vue'
import AppHeader from '@Widgets/AppHeader.vue'
import { LoaderFullScreenDomain } from '@UI/Loaders/LoaderFullScreenDomain'
import ModalGroupRetryPayment from '@components/Modals/RetryPayment/ModalGroupRetryPayment.vue'
import BannerGroupRetryPayment from '@Others/BannerRetryPayment/BannerGroupRetryPayment.vue'
import BannerUpsell from '@Widgets/UpsellBanner/BannerUpsell.vue'

defineOptions({
  name: 'LayoutDefault',
})

const customerStore = useCustomerStore()
const kidStore = useKidStore()
const subscriptionStore = useSubscriptionStore()
const commonStore = useCommonStore()

onBeforeMount(() => {
  useSettings()
  useLists()
  useSubscriptionList()
  kidStore.hydrateFromStorage()
  useStoreSubscriptionActions(() => useSubscriptionFailedApi().get())
})

useUpsellBannerLoad()

const headerSticky = useTemplateRef('headerSticky')
const { height: headerHeight } = useElementSize(headerSticky)

const mobileFooterSticky = useTemplateRef('mobileFooterSticky')
const { height: mobileFooterHeight } = useElementSize(mobileFooterSticky)

const footer = useTemplateRef('footer')
const { height: footerHeight } = useElementSize(footer)

// Page summaries leave the mobile footer for the footer slot at 820 (media_min_w_ipad_air).
const FOOTER_TABLET_MIN_WIDTH = 820

const { isMobile } = useScreenWidth({ tabletMinWidth: FOOTER_TABLET_MIN_WIDTH })

/** ws */
const $echo = useEcho()
useListenCustomerChannel($echo)
useListenDisciplineChannel($echo)
</script>

<template>
  <div
    v-if="customerStore.hasData"
    class="cs-app"
  >
    <div
      :style="{ height: `${headerHeight}px` }"
      class="cs-app-header"
    >
      <div
        ref="headerSticky"
        class="cs-header-sticky"
      >
        <BannerGroupRetryPayment
          v-if="subscriptionStore.isBannerGroupPaymentRetry"
        />
        <template v-else>
          <BannerUpsell />
          <div
            v-if="$slots.banner"
            class="cs-header-sticky__banner"
          >
            <slot name="banner" />
          </div>
        </template>
        <AppHeader />
      </div>
    </div>
    <div class="cs-app__container">
      <div
        :style="{
          '--header-and-footer-height': `${headerHeight + footerHeight + mobileFooterHeight}px`,
          '--header-and-mobile-footer-height': `${headerHeight + mobileFooterHeight}px`,
          '--mobile-footer-height': `${mobileFooterHeight}px`,
        }"
        :class="['cs-app__main', { landing: !!$slots.landing, 'no-scroll': commonStore.isNoScroll, 'footer-overlay': customerStore.isExp90 }]"
      >
        <div
          v-if="$slots.utilityButtons"
          :style="{ '--offset-top': `${headerHeight + 24}px` }"
          class="cs-app__utility-buttons"
        >
          <slot name="utilityButtons" />
        </div>
        <slot
          v-if="$slots.landing"
          name="landing"
        />
        <slot v-else />
      </div>
      <div
        v-if="$slots.footer"
        ref="footer"
        class="cs-app-footer"
      >
        <slot name="footer" />
      </div>
    </div>
    <div :class="['cs-app-mobile-footer', { overlay: customerStore.isExp90 }]">
      <div
        v-if="isMobile"
        ref="mobileFooterSticky"
        :class="['cs-footer-sticky', { 'low-index': commonStore.isModalShowed }]"
      >
        <MobileFooter>
          <template
            v-if="$slots.mobileFooter"
            #default
          >
            <slot name="mobileFooter" />
          </template>
        </MobileFooter>
      </div>
    </div>

    <!-- FIXME: Create COMMON modals, move ModalGroupRetryPayment to COMMON modals  -->
    <ModalGroupRetryPayment />
  </div>
  <div
    v-else
    class="cs-app loader"
  >
    <LoaderFullScreenDomain />
  </div>
</template>

<style scoped lang="scss" src="@scss/layouts/default.scss" />
